/**
 * Дозагрузка папок живого эфира (каждые 20 с): что из ответа сервера ложится в редактор.
 *
 * Раньше дозагрузка только ДОПИСЫВАЛА новые реплики, а каждая прежняя оставалась такой, какой её
 * загрузили, — и та, которой на сервере уже нет. Проба 20261103-P50m10d, эфир 8a8caa32, папка 1
 * en: редактор загрузил папку в 21:06:48, до озвучки; раскладка по фразам основного прохода
 * (21:07:30–34) разрезала фразу 20261103210219895344 (134460, слот 4120 мс) на куски
 * …210731211881 (134460) и …210732417352 и унесла родителя в `delete/`. Дозагрузка дописала куски
 * рядом с родителем, которого на сервере уже не было, и со старыми длинами всех реплик (слоты до
 * озвучки). ♪ соседней реплики запустил по этой копии раздвижку редактора: кусок встал за конец
 * мёртвого родителя (138580 + 50 = 138630, на 4170 мс от слова лектора), за ним цепочкой ещё
 * шесть реплик, и автосохранение записало это на диск под именем переводчика в 21:08:14 — после
 * того как «Выравнивание» папки уже было «пройдено». Строгий шаг остановил эфир.
 *
 * Теперь реплика, которую человек не правит (нет несохранённой правки), берётся такой, какой её
 * прислал сервер, а пропавшая с сервера — уходит и из редактора. Несохранённая правка не трогается
 * ни у какой реплики: строка остаётся локальной, даже если сервер её уже не знает (её сохранение
 * тогда откажет с причиной — правка человека не теряется молча).
 *
 * Правка или сохранение посреди опроса держат ТОЛЬКО свои реплики (`createLiveRowGuard`). Прежде такой
 * опрос оставлял все прежние реплики как были (только дописывал новые) — и так до первого опроса, за
 * время которого никто ничего не правил и не сохранял; пока человек работает, его может не быть
 * минутами. Проба 20261104-P50m10f, эфир b6afe338, папка 3 en: основной проход в 08:11:30 развёл наезд
 * каскадом, дубль …762856 встал на 125110 (слово лектора 124160), …762792 — 115800/8441; сборка папки
 * вернула их к слову лектора (115669 и 124160), на диске прежнее лежало только до 08:13:13 — а агент,
 * правивший в редакторе текст реплики …762214 (E2), в 08:16:08 видел на дорожке озвучки 125110 и
 * 115800/8441. Теперь сохраняемая реплика держится, остальные берутся с сервера на этом же опросе.
 */

// Папки и реплики — в том виде, в каком их отдаёт сервер (как и прежде, без строгого типа).
type Row = any;
type Folder = any;

export const liveRowId = (row: Row): string => String(row.id ?? row.original_id);

const sameRow = (a: Row, b: Row): boolean => {
    if (a === b) return true;
    try {
        return JSON.stringify(a) === JSON.stringify(b);
    } catch (e) {
        return false;
    }
};

/**
 * Прежняя дозагрузка: только новые реплики дописываются, прежние остаются с прежними полями,
 * папки дальше конца — целиком. Редактор её больше не зовёт (P50m10f: опрос посреди сохранения одной
 * реплики оставлял устаревшими все); она здесь для разбора P50m10d в тестах.
 */
export function mergeSubtitleFoldersAdditive(prev: Folder[] | undefined, fresh: Folder[]): any[] {
    const prevArr = prev || [];
    const out: Folder[] = [];
    for (let i = 0; i < fresh.length; i++) {
        const ff = fresh[i] || {};
        const pf = prevArr[i];
        if (!pf) {
            out.push(ff); // brand-new folder
            continue;
        }
        const existing = new Set<string>((pf.translations || []).map(liveRowId));
        const added = (ff.translations || []).filter((t: Row) => !existing.has(liveRowId(t)));
        out.push(added.length ? { ...pf, translations: [...(pf.translations || []), ...added] } : pf);
    }
    for (let i = fresh.length; i < prevArr.length; i++) out.push(prevArr[i]);
    return out;
}

/**
 * Дозагрузка по серверу: реплика без несохранённой правки (`keepLocal(id)` — false) — такая, как на
 * сервере; пропавшая с сервера — уходит; с правкой — остаётся локальной. Неизменившаяся реплика и
 * неизменившаяся папка остаются теми же объектами: дорожки не перерисовываются каждые 20 с.
 */
export function mergeSubtitleFoldersLive(
    prev: Folder[] | undefined,
    fresh: Folder[],
    keepLocal: (id: string) => boolean,
): any[] {
    const prevArr = prev || [];
    const out: Folder[] = [];
    for (let i = 0; i < fresh.length; i++) {
        const ff = fresh[i] || {};
        const pf = prevArr[i];
        if (!pf) {
            out.push(ff);
            continue;
        }
        const prevRows = pf.translations || [];
        // Без кортежных типов: сборка редактора (CRA 3, eslint-парсер TS 3.4) падает на них при TypeScript 5.
        const prevById = new Map<string, Row>();
        prevRows.forEach((t: Row) => prevById.set(liveRowId(t), t));
        const freshRows = ff.translations || [];
        const freshIds = new Set<string>(freshRows.map(liveRowId));
        const rows: Row[] = freshRows.map((t: Row) => {
            const old = prevById.get(liveRowId(t));
            if (old && (keepLocal(liveRowId(t)) || sameRow(old, t))) return old;
            return t;
        });
        // Правка человека у реплики, которой сервер уже не прислал, не теряется.
        prevRows.forEach((t: Row) => {
            if (!freshIds.has(liveRowId(t)) && keepLocal(liveRowId(t))) rows.push(t);
        });
        const changed = rows.length !== prevRows.length || rows.some((r: Row, j: number) => r !== prevRows[j]);
        out.push(changed ? { ...pf, translations: rows } : pf);
    }
    for (let i = fresh.length; i < prevArr.length; i++) out.push(prevArr[i]);
    return out;
}

/**
 * Какие реплики опрос эфира оставляет такими, как в редакторе, хотя несохранённой правки у них нет.
 *
 * Ответ опроса мог уйти с сервера раньше, чем легла правка: реплику, которую тронули (пометили
 * несохранённой — человек или машинный сдвиг) или сохраняли, пока запрос был в пути, он вернул бы
 * на экран прежней. Такая реплика держится до следующего опроса. Остальные — с сервера: чужое
 * сохранение их не держит.
 *
 * `now()` — перед запросом опроса; `heldSince(тот номер, язык, id)` — после ответа.
 */
export interface LiveRowGuard {
    now: () => number;
    /** Реплику тронули в редакторе (пометили несохранённой). */
    touch: (lang: string, id: string) => void;
    /** Реплики ушли на сервер; вернувшаяся функция — сохранение кончилось (удачно или нет). */
    beginSave: (rows: { lang: string; id: string }[]) => () => void;
    heldSince: (since: number, lang: string, id: string) => boolean;
}

export function createLiveRowGuard(): LiveRowGuard {
    let epoch = 0;
    const touched = new Map<string, number>();
    const saving = new Map<string, number>();
    const keyOf = (lang: string, id: string) => `${lang}:${id}`;
    const touchKey = (key: string) => {
        epoch += 1;
        touched.set(key, epoch);
    };
    return {
        now: () => epoch,
        touch: (lang, id) => touchKey(keyOf(lang, id)),
        beginSave: (rows) => {
            const keys = Array.from(new Set(rows.map((r) => keyOf(r.lang, String(r.id)))));
            keys.forEach((key) => {
                touchKey(key);
                saving.set(key, (saving.get(key) || 0) + 1);
            });
            let done = false;
            return () => {
                if (done) return;
                done = true;
                keys.forEach((key) => {
                    const left = (saving.get(key) || 1) - 1;
                    if (left > 0) saving.set(key, left);
                    else saving.delete(key);
                    // Опрос, ушедший во время сохранения, мог получить ответ до записи.
                    touchKey(key);
                });
            };
        },
        heldSince: (since, lang, id) => {
            const key = keyOf(lang, id);
            return saving.has(key) || (touched.get(key) || 0) > since;
        },
    };
}

/** Есть ли у реплики `id` языка `lang` несохранённая правка (человека или машинный сдвиг). */
export const hasUnsavedChange = (dirtyKeys: Iterable<string>, lang: string, id: string): boolean => {
    const own = `${lang}:${id}`;
    for (const key of Array.from(dirtyKeys)) {
        if (key === own || key.startsWith(`${own}@`)) return true;
    }
    return false;
};
